<template>
  <div class="min-h-screen bg-background">
    <!-- Header -->
    <div class="sticky top-0 bg-background/95 backdrop-blur-sm z-10 px-6 pt-12 pb-4 border-b border-gray-100">
      <div class="flex items-center">
        <button @click="router.back()" class="p-2 -ml-2 text-textMuted">
          <span class="text-xl">←</span>
        </button>
      </div>
    </div>

    <div v-if="vaccine" class="p-6 pb-32">
      <!-- 疫苗標題卡片 -->
      <div class="bg-white rounded-2xl p-6 shadow-sm border border-gray-50 mb-6">
        <div class="flex items-start justify-between gap-3 mb-3">
          <h2 class="text-2xl font-bold text-textMain flex-1 min-w-0">
            {{ vaccine.name }}
          </h2>
          <span 
            class="px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap flex-shrink-0"
            :class="vaccine.type === 'public' || vaccine.type === 'mixed'
              ? 'bg-green-100 text-green-700'
              : 'bg-orange-100 text-orange-700'"
          >
            {{ vaccine.type === 'public' || vaccine.type === 'mixed' ? '公費' : '自費' }}
          </span>
        </div>
        <p v-if="vaccine.fullName" class="text-sm text-textMuted">
          {{ vaccine.fullName }}
        </p>
      </div>

      <!-- 疫苗作用 -->
      <div class="bg-white rounded-2xl p-6 shadow-sm border border-gray-50 mb-6">
        <div class="flex items-center gap-2 mb-4">
          <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <span class="text-xl">🛡️</span>
          </div>
          <h3 class="text-lg font-bold text-textMain">疫苗作用</h3>
        </div>
        <p class="text-textMain leading-relaxed">
          {{ vaccine.description }}
        </p>
      </div>

      <!-- 副作用 / 常見反應 -->
      <div class="bg-white rounded-2xl p-6 shadow-sm border border-gray-50 mb-6">
        <div class="flex items-center gap-2 mb-4">
          <div class="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <span class="text-xl">🌡️</span>
          </div>
          <h3 class="text-lg font-bold text-textMain">副作用 / 常見反應</h3>
        </div>
        
        <!-- 常見反應列表 -->
        <div class="grid gap-3">
          <div 
            v-for="(effect, index) in vaccine.sideEffects" 
            :key="index"
            class="flex items-center gap-2 text-sm text-textMain"
          >
            <span class="text-gray-400">✓</span>
            <span>{{ effect }}</span>
          </div>
        </div>
      </div>

      <!-- 劑次資訊 -->
      <!-- 已移除接種時程區塊 -->

      <!-- 備註 -->
      <div v-if="vaccine.notes && vaccine.notes.length > 0" class="bg-amber-50 rounded-2xl p-6 border border-amber-100 mb-6">
        <div class="flex items-start gap-2">
          <span class="text-amber-500 mt-0.5">💡</span>
          <div class="flex-1">
            <h3 class="font-bold text-amber-900 mb-2">注意事項</h3>
            <ul class="space-y-1">
              <li 
                v-for="(note, index) in vaccine.notes" 
                :key="index"
                class="text-sm text-amber-800 leading-relaxed"
              >
                • {{ note }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- 前往疾管署按鈕 -->
      <a
        href="https://www.cdc.gov.tw/"
        target="_blank"
        rel="noopener noreferrer"
        class="block w-full bg-emerald-600 text-white font-bold py-4 rounded-2xl text-center shadow-lg hover:bg-emerald-700 transition-colors"
      >
        前往衛福部疾管署網站查詢詳細資訊 ↗
      </a>
    </div>

    <!-- Loading 狀態 -->
    <div v-else class="flex items-center justify-center min-h-screen">
      <div class="text-center">
        <div class="text-4xl mb-4">💉</div>
        <p class="text-textMuted">載入中...</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useVaccineDefinitions } from '../stores/useVaccineDefinitions';
import type { Vaccine, DoseDefinition } from '../types';

const router = useRouter();
const route = useRoute();
const vaccineDefinitions = useVaccineDefinitions();

const vaccine = ref<Vaccine | null>(null);
const doses = ref<DoseDefinition[]>([]);

onMounted(() => {
  const vaccineId = route.params.id as string;

  if (vaccineId) {
    const v = vaccineDefinitions.getVaccineById(vaccineId);
    if (v) {
      vaccine.value = v;
      doses.value = vaccineDefinitions.getDosesByVaccineId(vaccineId);
    } else {
      console.error('Vaccine not found:', vaccineId);
    }
  }
});
</script>

<style scoped>
/* 確保在 iOS 上底部按鈕不會被遮住 */
@supports (padding-bottom: env(safe-area-inset-bottom)) {
  .pb-32 {
    padding-bottom: calc(8rem + env(safe-area-inset-bottom));
  }
}
</style>